import { useEffect, useRef, useState } from "react";

import { CalendarRange, Check } from "lucide-react";

import {
  aplicarPreset,
  comData,
  periodoResumo,
  presetsRapidos,
  PRESET_LABEL
} from "../lib/periodo";
import { cn } from "../lib/utils";

export function PeriodoFiltro({ estado, ano, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setAberto(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [aberto]);

  const presets = presetsRapidos(ano);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAberto(v => !v)}
        aria-expanded={aberto}
        aria-label="Filtro de período"
        title={periodoResumo(estado)}
        className={cn(
          "inline-flex items-center gap-2 rounded-xl border p-2 text-[12px] font-medium transition-all sm:px-3 sm:py-2",
          "border-transparent bg-[var(--color-surface-2)]/60 text-[var(--color-ink-soft)]",
          "hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
          aberto &&
            "border-[var(--color-brand)] bg-[var(--color-surface-2)] text-[var(--color-ink)] shadow-sm"
        )}
      >
        <CalendarRange
          size={14}
          className="flex-none text-[var(--color-brand)]"
        />
        <span className="hidden truncate sm:inline">
          {periodoResumo(estado)}
        </span>
      </button>

      {aberto && (
        <div
          className={cn(
            "absolute right-0 z-50 mt-2 w-[300px] max-w-[calc(100vw-2rem)] rounded-2xl border p-4",
            "border-[var(--color-border-strong)] bg-[var(--color-surface)] shadow-2xl shadow-black/20 backdrop-blur-xl"
          )}
        >
          {/* Atalhos rápidos */}
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-ink-faint)]">
            Atalhos
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presets.map(p => {
              const ativo = estado.preset === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onChange(aplicarPreset(p, ano))}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-all",
                    ativo
                      ? "border-[var(--color-brand)] bg-[var(--color-brand-dim)] text-[var(--color-brand)]"
                      : "border-transparent bg-[var(--color-surface-2)]/60 text-[var(--color-ink-soft)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
                  )}
                >
                  {ativo && <Check size={12} />}
                  {rotuloPreset(p)}
                </button>
              );
            })}
          </div>

          {/* Intervalo pelo calendário */}
          <div className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-ink-faint)]">
            Escolher no calendário
          </div>
          <div className="space-y-2.5">
            <DataInput
              label="De"
              value={estado.inicio ?? ""}
              max={estado.fim}
              onChange={v => onChange(comData(estado, "inicio", v))}
            />

            <DataInput
              label="Até"
              value={estado.fim ?? ""}
              min={estado.inicio}
              onChange={v => onChange(comData(estado, "fim", v))}
            />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-[var(--color-ink-faint)]">
            Escolha o mesmo dia em “De” e “Até” para ver um único dia, ou um
            intervalo para semana, mês ou período maior.
          </p>
        </div>
      )}
    </div>
  );
}

function rotuloPreset(p) {
  const curto = {
    ano: "Ano inteiro",
    "7d": "7 dias",
    "30d": "30 dias",
    "90d": "90 dias"
  };
  return curto[p] ?? PRESET_LABEL[p];
}

function DataInput({ label, value, min, max, onChange }) {
  return (
    <label className="flex items-center gap-3">
      <span className="w-8 flex-none text-[12px] text-[var(--color-ink-soft)]">
        {label}
      </span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={e => onChange(e.target.value)}
        className={cn(
          "min-w-0 flex-1 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface-2)] px-2.5 py-1.5",
          "text-[12.5px] text-[var(--color-ink)] outline-none transition-all",
          "focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[var(--color-brand)]/30"
        )}
      />
    </label>
  );
}
